import type {ReactNode} from 'react';
import {Link} from '@tanstack/react-router';
import {History, SquarePen} from 'lucide-react';
import {Button} from '@/shared/ui/components/button';
import {SheetTrigger} from '@/shared/ui/components/sheet';

export type ChatPanelProps = {
  readonly title: string;
  readonly children: ReactNode;
};

/**
 * The chat column: a 40px header row (title, History, New chat) directly
 * under the shell header, then the transcript and the composer supplied as
 * children. Rendered inside `AssistantPage`'s `Sheet` root: the History
 * button is the sheet's trigger so Radix returns focus to it on close (a
 * modal dialog focuses nothing without a trigger).
 */
export const ChatPanel = ({title, children}: ChatPanelProps) => (
  <section aria-label={title} className="flex min-h-0 flex-1 flex-col">
    <div className="flex h-10 shrink-0 items-center gap-1 border-b px-4 lg:px-6">
      <h1 className="min-w-0 flex-1 truncate text-base font-medium">{title}</h1>
      <SheetTrigger asChild>
        <Button type="button" variant="ghost" size="icon" aria-label="Conversation history">
          <History aria-hidden="true" />
        </Button>
      </SheetTrigger>
      <Button variant="ghost" size="icon" asChild>
        <Link
          to="/assistant/{-$conversationId}"
          params={{conversationId: undefined}}
          aria-label="New chat"
        >
          <SquarePen aria-hidden="true" />
        </Link>
      </Button>
    </div>
    {children}
  </section>
);

/** Centres the composer in the transcript's column; 12px side margins on phones. */
export const ChatPanelFooter = ({children}: {readonly children: ReactNode}) => (
  <div className="mx-auto flex w-full max-w-3xl shrink-0 flex-col gap-2 px-3 pb-3 md:px-4 md:pb-4">
    {children}
  </div>
);
